import { useMutation, useQuery } from '@apollo/client';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAgent } from '../../hooks/use-agent';

import { client as apolloClient } from '../../graphql/apollo-client';
import {
    AgentDraftClientUpdate,
    AgentDraftJobUpdate,
    APPLY_CLIENT_UPDATE_DRAFT,
    APPLY_JOB_UPDATE_DRAFT,
    DISMISS_CLIENT_UPDATE_DRAFT,
    DISMISS_JOB_UPDATE_DRAFT,
    GET_AGENT_DRAFT_CLIENT_UPDATE,
    GET_AGENT_DRAFT_JOB_UPDATE,
    UNDO_CLIENT_UPDATE_DRAFT_APPLY,
    UNDO_CLIENT_UPDATE_DRAFT_DISMISS,
    UNDO_JOB_UPDATE_DRAFT_APPLY,
    UNDO_JOB_UPDATE_DRAFT_DISMISS
} from '../../graphql/queries/agent';
import { CLIENT_UPDATE } from '../../graphql/queries/clients';
import { UPDATE_JOB_WITH_CLIENT_AND_DESCRIPTION } from '../../graphql/queries/job-form';
import { useSession } from '../../hooks/use-session';
import { useSnackbar } from '../../hooks/use-snackbar';
import {
    checkStaleness,
    CLIENT_FIELD_LABELS,
    DiffSection,
    DiffSectionView,
    JOB_FIELD_LABELS,
    updateDiffDialogStyles
} from './update-diff-utils';

const SKELETON_HEIGHT = 200;

export interface UpdateDiffDialogProps {
    open: boolean;
    onClose: () => void;
    draftId: string;
    draftType: 'client' | 'job';
}

export const UpdateDiffDialog: React.FC<UpdateDiffDialogProps> = ({ open, onClose, draftId, draftType }) => {
    const theme = useTheme();
    const rootStyles = updateDiffDialogStyles(theme);
    const history = useHistory();
    const location = useLocation();
    const agent = useAgent();
    const session = useSession();
    const userId = session?.user?.id;
    const { setSnackbar } = useSnackbar();

    // Track if we detected staleness during apply attempt (race condition)
    const [detectedStale, setDetectedStale] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setDetectedStale(false);
        }
    }, [open, draftId, draftType]);

    const {
        data: clientData,
        loading: clientLoading,
        error: clientError,
        refetch: refetchClient
    } = useQuery<{
        draft: AgentDraftClientUpdate | null;
    }>(GET_AGENT_DRAFT_CLIENT_UPDATE, {
        skip: draftType !== 'client',
        variables: { id: draftId }
    });

    const {
        data: jobData,
        loading: jobLoading,
        error: jobError,
        refetch: refetchJob
    } = useQuery<{
        draft: AgentDraftJobUpdate | null;
    }>(GET_AGENT_DRAFT_JOB_UPDATE, {
        skip: draftType !== 'job',
        variables: { id: draftId }
    });

    // Mutations for marking drafts as applied/dismissed
    const [applyClientDraftMutation, { loading: applyingClientDraft }] = useMutation(APPLY_CLIENT_UPDATE_DRAFT);
    const [dismissClientMutation, { loading: dismissingClient }] = useMutation(DISMISS_CLIENT_UPDATE_DRAFT);
    const [applyJobDraftMutation, { loading: applyingJobDraft }] = useMutation(APPLY_JOB_UPDATE_DRAFT);
    const [dismissJobMutation, { loading: dismissingJob }] = useMutation(DISMISS_JOB_UPDATE_DRAFT);

    // Mutations for undoing apply/dismiss
    const [undoClientApplyMutation] = useMutation(UNDO_CLIENT_UPDATE_DRAFT_APPLY);
    const [undoClientDismissMutation] = useMutation(UNDO_CLIENT_UPDATE_DRAFT_DISMISS);
    const [undoJobApplyMutation] = useMutation(UNDO_JOB_UPDATE_DRAFT_APPLY);
    const [undoJobDismissMutation] = useMutation(UNDO_JOB_UPDATE_DRAFT_DISMISS);

    // Mutations for actually updating the data (with role-based clients)
    const clientCreatorClient = React.useMemo(() => apolloClient('client_creator'), []);
    const jobCreatorClient = React.useMemo(() => apolloClient('job_creator'), []);
    const [updateClientMutation, { loading: updatingClient }] = useMutation(CLIENT_UPDATE, {
        client: clientCreatorClient
    });
    const [updateJobMutation, { loading: updatingJob }] = useMutation(UPDATE_JOB_WITH_CLIENT_AND_DESCRIPTION, {
        client: jobCreatorClient
    });

    const loading = clientLoading || jobLoading;
    const error = clientError || jobError;
    const applying = applyingClientDraft || applyingJobDraft || updatingClient || updatingJob;
    const dismissing = dismissingClient || dismissingJob;

    // Check if user is account manager for this job/client
    const accountManagerId =
        draftType === 'client' ? clientData?.draft?.client?.accountManagerId : jobData?.draft?.job?.accountManagerId;
    const isAccountManager = userId === accountManagerId;

    // Permission checks
    const canApplyClientUpdates = session?.userPermissions?.client_creator ?? false;
    const canApplyJobUpdates = session?.userPermissions?.job_creator ?? false;
    const canApply = draftType === 'client' ? canApplyClientUpdates : canApplyJobUpdates;

    // Extract data based on type
    const draft = draftType === 'client' ? clientData?.draft : jobData?.draft;
    const refetch = draftType === 'client' ? refetchClient : refetchJob;

    const handleApply = React.useCallback(async () => {
        if (!userId || applying) return;

        // Refetch current data and re-check staleness before applying
        const { data: freshData } = await refetch();

        if (draftType === 'client') {
            const d = (freshData as { draft: AgentDraftClientUpdate | null })?.draft;
            if (d && checkStaleness(d.updates.old || {}, d.client as Record<string, unknown> | null)) {
                setDetectedStale(true);
                return; // Cancel apply - data changed
            }
            // Apply the actual data changes
            if (d?.updates.new && Object.keys(d.updates.new).length > 0) {
                await updateClientMutation({
                    variables: { clientId: d.clientId, updates: d.updates.new }
                });
            }
            await applyClientDraftMutation({
                variables: { id: draftId, appliedAt: Date.now(), appliedBy: userId }
            });

            // Create undo handler with captured draft data
            const undoApply = async () => {
                if (d?.updates.old && Object.keys(d.updates.old).length > 0) {
                    await updateClientMutation({
                        variables: { clientId: d.clientId, updates: d.updates.old }
                    });
                }
                await undoClientApplyMutation({ variables: { id: draftId } });
                refetch();
                setSnackbar('Changes reverted');
            };
            const undoButton = (
                <Button onClick={undoApply} style={{ color: 'white' }}>
                    Undo
                </Button>
            );
            setSnackbar('Changes applied', undoButton);
        } else if (draftType === 'job') {
            const d = (freshData as { draft: AgentDraftJobUpdate | null })?.draft;
            let hasJobUpdates = false;
            let hasJobDescriptionUpdates = false;
            if (d) {
                let nowStale = false;
                if (d.updates.job) {
                    nowStale = checkStaleness(d.updates.job.old, d.job as Record<string, unknown> | null);
                }
                if (!nowStale && d.updates.jobDescription) {
                    nowStale = checkStaleness(
                        d.updates.jobDescription.old,
                        d.job?.description as Record<string, unknown> | null
                    );
                }
                if (nowStale) {
                    setDetectedStale(true);
                    return; // Cancel apply - data changed
                }
                hasJobUpdates = Boolean(d.updates.job && Object.keys(d.updates.job.new).length > 0);
                hasJobDescriptionUpdates = Boolean(
                    d.updates.jobDescription && Object.keys(d.updates.jobDescription.new).length > 0
                );
            }
            // Apply the actual data changes
            if (d) {
                if (hasJobUpdates || hasJobDescriptionUpdates) {
                    await updateJobMutation({
                        variables: {
                            client: {},
                            clientId: d.job?.clientId,
                            description: d.updates.jobDescription?.new || {},
                            job: d.updates.job?.new || {},
                            jobId: d.jobId
                        }
                    });
                }
                await applyJobDraftMutation({
                    variables: { id: draftId, appliedAt: Date.now(), appliedBy: userId }
                });

                // Create undo handler with captured draft data
                const undoApply = async () => {
                    await updateJobMutation({
                        variables: {
                            client: {},
                            clientId: d.job?.clientId,
                            description: d.updates.jobDescription?.old || {},
                            job: d.updates.job?.old || {},
                            jobId: d.jobId
                        }
                    });
                    await undoJobApplyMutation({ variables: { id: draftId } });
                    refetch();
                    setSnackbar('Changes reverted');
                };
                const undoButton = (
                    <Button onClick={undoApply} style={{ color: 'white' }}>
                        Undo
                    </Button>
                );
                setSnackbar('Changes applied', undoButton);
            }
        }

        refetch();
        onClose();
    }, [
        userId,
        applying,
        draftId,
        draftType,
        updateClientMutation,
        updateJobMutation,
        applyClientDraftMutation,
        applyJobDraftMutation,
        undoClientApplyMutation,
        undoJobApplyMutation,
        refetch,
        onClose,
        setSnackbar
    ]);

    const handleDismiss = React.useCallback(async () => {
        if (!userId) return;
        const variables = { id: draftId, dismissedAt: Date.now(), dismissedBy: userId };
        if (draftType === 'client') {
            await dismissClientMutation({ variables });
            // Create undo handler with captured draftId
            const undoDismiss = async () => {
                await undoClientDismissMutation({ variables: { id: draftId } });
                refetch();
                setSnackbar('Draft restored');
            };
            const undoButton = (
                <Button onClick={undoDismiss} style={{ color: 'white' }}>
                    Undo
                </Button>
            );
            setSnackbar('Draft dismissed', undoButton);
        } else {
            await dismissJobMutation({ variables });
            // Create undo handler with captured draftId
            const undoDismiss = async () => {
                await undoJobDismissMutation({ variables: { id: draftId } });
                refetch();
                setSnackbar('Draft restored');
            };
            const undoButton = (
                <Button onClick={undoDismiss} style={{ color: 'white' }}>
                    Undo
                </Button>
            );
            setSnackbar('Draft dismissed', undoButton);
        }
        refetch();
        onClose();
    }, [
        userId,
        draftId,
        draftType,
        dismissClientMutation,
        dismissJobMutation,
        undoClientDismissMutation,
        undoJobDismissMutation,
        refetch,
        onClose,
        setSnackbar
    ]);

    const handleEdit = React.useCallback(() => {
        const conversationId =
            draftType === 'client' ? clientData?.draft?.conversationId : jobData?.draft?.conversationId;
        if (conversationId) {
            if (location.pathname.startsWith('/orbit')) {
                // On orbit page - navigate to the conversation URL
                history.push(`/orbit/chat/${conversationId}`);
            } else {
                // Not on orbit page - open sidebar and select conversation
                agent.onSelectConversation(conversationId);
                agent.onOpen();
            }
        }
        onClose();
    }, [draftType, clientData, jobData, location.pathname, history, agent, onClose]);

    // Build title and sections based on type
    let dialogTitle = '';
    const sections: DiffSection[] = [];

    if (draftType === 'client' && clientData?.draft) {
        const d = clientData.draft;
        dialogTitle = `Client Update: ${d.client?.name || 'Unknown Client'}`;
        if (d.updates.new && Object.keys(d.updates.new).length > 0) {
            sections.push({
                fieldLabels: CLIENT_FIELD_LABELS,
                newValues: d.updates.new,
                oldValues: d.updates.old || {},
                title: 'Client Information'
            });
        }
    } else if (draftType === 'job' && jobData?.draft) {
        const d = jobData.draft;
        const jobTitle = d.job?.title || 'Unknown Job';
        const clientName = d.job?.client?.name || '';
        dialogTitle = clientName ? `Job Update: ${jobTitle} @ ${clientName}` : `Job Update: ${jobTitle}`;
        if (d.updates.job && Object.keys(d.updates.job.new).length > 0) {
            sections.push({
                fieldLabels: JOB_FIELD_LABELS,
                newValues: d.updates.job.new,
                oldValues: d.updates.job.old,
                title: 'Job Settings'
            });
        }
        if (d.updates.jobDescription && Object.keys(d.updates.jobDescription.new).length > 0) {
            sections.push({
                fieldLabels: JOB_FIELD_LABELS,
                newValues: d.updates.jobDescription.new,
                oldValues: d.updates.jobDescription.old,
                title: 'Job Description'
            });
        }
    }

    const isResolved = Boolean(draft?.appliedAt || draft?.dismissedAt);
    const explanation = draft?.explanation;
    const sourceDescription = draft?.sourceDescription;

    // Detect staleness: compare stored "old" values vs current data
    // Also include runtime-detected staleness from apply attempt
    let computedStale = false;
    if (!isResolved) {
        if (draftType === 'client' && clientData?.draft) {
            const d = clientData.draft;
            const currentClient = d.client as Record<string, unknown> | null;
            computedStale = checkStaleness(d.updates.old || {}, currentClient);
        } else if (draftType === 'job' && jobData?.draft) {
            const d = jobData.draft;
            const currentJob = d.job as Record<string, unknown> | null;
            const currentJobDesc = d.job?.description as Record<string, unknown> | null;
            if (d.updates.job) {
                computedStale = checkStaleness(d.updates.job.old, currentJob);
            }
            if (!computedStale && d.updates.jobDescription) {
                computedStale = checkStaleness(d.updates.jobDescription.old, currentJobDesc);
            }
        }
    }
    const isStale = computedStale || detectedStale;

    const sectionElements = sections.map((section, sectionIdx) => (
        <DiffSectionView key={sectionIdx} section={section} />
    ));

    const explanationEl = explanation ? (
        <Typography variant="body1" className="explanation">
            {explanation}
        </Typography>
    ) : null;

    const sourceEl = sourceDescription ? (
        <Typography variant="body2" color="textSecondary" className="source">
            Source: {sourceDescription}
        </Typography>
    ) : null;

    const loadingContent = (
        <DialogContent dividers={true} className="content">
            <Skeleton variant="rect" width="100%" height={SKELETON_HEIGHT} />
        </DialogContent>
    );

    const errorContent = (
        <DialogContent dividers={true} className="content">
            <Typography color="error">Failed to load draft. Please try again.</Typography>
        </DialogContent>
    );

    const mainContent = (
        <DialogContent dividers={true} className="content">
            {explanationEl}
            {sourceEl}
            {sectionElements}
        </DialogContent>
    );

    const statusBadge = draft?.appliedAt ? (
        <span className="badge badge--applied">Applied</span>
    ) : draft?.dismissedAt ? (
        <span className="badge badge--dismissed">Dismissed</span>
    ) : isStale ? (
        <span className="badge badge--stale">Stale</span>
    ) : null;

    const busy = applying || dismissing;
    const showActions = !error;
    const showStaleActions = !isResolved && isStale && isAccountManager;
    const showNormalActions = !isResolved && !isStale && isAccountManager;
    const permissionTip = `Requires ${draftType === 'client' ? 'client_creator' : 'job_creator'} permission`;
    const dismissBtn = (
        <Button onClick={handleDismiss} disabled={busy}>
            Dismiss
        </Button>
    );
    const rejectBtn = (
        <Button onClick={handleDismiss} disabled={busy}>
            Reject
        </Button>
    );
    const acceptBtn = (
        <Button onClick={handleApply} disabled={!canApply || busy}>
            Accept
        </Button>
    );
    const editBtn = (
        <Button onClick={handleEdit} disabled={busy}>
            Edit
        </Button>
    );
    const wrappedAcceptBtn = canApply ? (
        acceptBtn
    ) : (
        <Tooltip title={permissionTip}>
            <span>{acceptBtn}</span>
        </Tooltip>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth={true} css={rootStyles}>
            <DialogTitle disableTypography={true} className="title">
                <Typography variant="h4" component="div">
                    {loading ? 'Loading...' : dialogTitle}
                </Typography>
                {(isResolved || isStale) && statusBadge}
            </DialogTitle>
            {loading ? loadingContent : error ? errorContent : mainContent}
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {showActions && showStaleActions && dismissBtn}
                {showActions && showNormalActions && rejectBtn}
                {showActions && !isResolved && isAccountManager && editBtn}
                {showActions && showNormalActions && wrappedAcceptBtn}
            </DialogActions>
        </Dialog>
    );
};
